import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { fetchHandbook, HandbookLang, HandbookScene, Localized } from '../../api/handbook';
import PanelHeader from '../../components/PanelHeader';
import ScanFrame from '../../components/ScanFrame';
import { C, FONT, PANEL_RADIUS, depthSx, hexToRgba, lcarsLabelSx, prefersReducedMotion } from '../../theme/tokens';
import BlockView from './HandbookBlocks';
import ChainCanvas from './ChainCanvas';

/*
  THESIS: the corp's handbook is doctrine you read once and then act on under
  time pressure, so this is a reading surface with a live model beside it —
  not a PDF viewer and not a stack of collapsed accordions.
  OWN-WORLD: unchanged v13 Wormhole Console. Void ground, wormhole purple as
  identity, scanner cyan as the readout, ScanFrame panels, Orbitron/Rajdhani/
  Inter/Share Tech Mono. LCARS is one elbow tab on the rules the document
  itself sets apart — the sanctioned shape, nothing structural.
  STORY: a member opens it to answer one question, finds the chapter from the
  rail, and leaves knowing the mechanic because the chain beside the text was
  doing that mechanic while they read.
  FIRST VIEWPORT: masthead — title in Orbitron inside a bracket frame, the
  document's own ASCII topology beside a meta strip, the guiding principle as
  the first elbow-tabbed rule, then the chapter index.
  FORM: reading column with a persistent chapter rail and a scroll-driven
  companion canvas. Extension of an established surface — no world change.
*/

const LANG_KEY = 'gobbo.handbook.lang';
const COMPANION_KEY = 'gobbo.handbook.companion';

/** Sticky offsets clear the floating nav capsule (see components/Layout). */
const STICKY_TOP = { xs: 72, md: 82 };

const say = (v: Localized, lang: HandbookLang) => v[lang];

/** One caption per canvas state, so the model is never a mystery picture. */
const SCENE_CAPTION: Record<HandbookScene, Localized> = {
  chain: { de: 'Home und was daran hängt', en: 'Home, and what hangs off it' },
  spawn: { de: 'Wie eine K162 entsteht', en: 'How a K162 comes into being' },
  scan: { de: 'Probes lösen Signaturen auf', en: 'Probes resolving signatures' },
  mass: { de: 'Masse bis zum Collapse', en: 'Mass, down to collapse' },
  polarised: { de: 'Die Richtungssperre', en: 'The direction lock' },
  threat: { de: 'D-Scan und Ampel', en: 'D-scan and the traffic light' },
  sites: { de: 'Site-Grid mit Spawn-Timer', en: 'A site grid on a spawn timer' },
  route: { de: 'Der Weg zum Handelszentrum', en: 'The run to the trade hub' },
  chaindepth: { de: 'Die Kette, mehrere Hops tief', en: 'The chain, several hops out' },
  holecontrol: { de: 'Was durch das Loch kommt', en: 'What comes through the hole' },
  homedefence: { de: 'Hostile am Home-Hole', en: 'A hostile on the home hole' },
  types: { de: 'Die Verbindungsarten', en: 'The kinds of connection' },
  mapping: { de: 'Die Kette entdecken und benennen', en: 'Discovering the chain, and naming it' },
  rolling: { de: 'Ein Roller kollabiert das Loch', en: 'A roller collapsing the hole' },
  rollnormal: { de: 'Normal Roll, Sprung für Sprung', en: 'The normal roll, transit by transit' },
  rollshrink: { de: 'Shrink Roll auf einem reduzierten Loch', en: 'The shrink roll, on a reduced hole' },
  rollrage: { de: 'Rage Roll — alle auf einmal', en: 'The rage roll — everyone at once' },
  emergency: { de: 'Verloren — einen Ausgang scannen', en: 'Lost — scanning a way out' },
  roles: { de: 'Rollen und Funkdisziplin', en: 'Roles and what they call' },
  licence: { de: 'Die drei Stufen', en: 'The three levels' },
  checklist: { de: 'Abhaken, bevor es losgeht', en: 'Ticking off before you go' },
  closing: { de: 'Die Kette in Ruhe', en: 'The chain at rest' },
};

/** The page's one authored entrance, reused per chapter as it arrives. A
 * clip wipe rather than a fade: the handbook reads as pages being turned to,
 * which is the vault's `unseal` idea applied to a document. */
const revealSx = {
  opacity: 0,
  transform: 'translateY(14px)',
  transition: prefersReducedMotion()
    ? 'none'
    : 'opacity 0.42s cubic-bezier(0.16,1,0.3,1), transform 0.42s cubic-bezier(0.16,1,0.3,1)',
  '&[data-in="true"]': { opacity: 1, transform: 'none' },
  // Reduced motion never hides content behind a transition it will not run.
  '@media (prefers-reduced-motion: reduce)': { opacity: 1, transform: 'none' },
} as const;

const MetaCell: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={lcarsLabelSx(hexToRgba(C.cyan, 0.8), '0.62rem')}>{label}</Typography>
    <Typography
      sx={{ fontFamily: FONT.mono, fontSize: '0.8rem', color: C.text, mt: 0.35, lineHeight: 1.35 }}
    >
      {value}
    </Typography>
  </Box>
);

const Handbook: React.FC = () => {
  const { data, isLoading, isError } = useQuery({ queryKey: ['handbook'], queryFn: fetchHandbook });

  const [lang, setLang] = useState<HandbookLang>(() => {
    try {
      return window.localStorage.getItem(LANG_KEY) === 'de' ? 'de' : 'en';
    } catch {
      return 'en';
    }
  });
  const setLanguage = useCallback((next: HandbookLang) => {
    setLang(next);
    try {
      window.localStorage.setItem(LANG_KEY, next);
    } catch {
      /* storage blocked: the choice still holds for this session */
    }
  }, []);

  /** On a phone the companion is worth roughly a fifth of the viewport, so
   * it is the reader's call whether to spend it. Their choice sticks. */
  const [companionOpen, setCompanionOpen] = useState(() => {
    try {
      return window.localStorage.getItem(COMPANION_KEY) !== 'closed';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(COMPANION_KEY, companionOpen ? 'open' : 'closed');
    } catch {
      /* storage blocked: the toggle still works for this session */
    }
  }, [companionOpen]);

  const [activeId, setActiveId] = useState<string | null>(null);
  /** The scene the panel is showing. Anchored per *section*, not per chapter:
   * chapter 17's three emergencies and chapter 20's four checklists are each
   * about a different mechanic, and the panel has to follow what is under the
   * reader's eye. Derived in the same scroll pass from `[data-scene]`. */
  const [scene, setScene] = useState<HandbookScene>('chain');
  /** Mirrors `scene` for the measure pass, which needs the committed value
   * without re-creating its effect on every change. */
  const sceneRef = useRef<HandbookScene>('chain');
  /**
   * The settled scene: what the panel draws AND what its caption names.
   *
   * The measured value can change a few times in quick succession while the
   * column reflows, so it is debounced once, here, and both consumers read
   * the result. Debouncing inside the canvas instead let the caption run
   * ahead of the picture beside it — the panel drew one chapter's diagram
   * under the next chapter's label.
   */
  const [shownScene, setShownScene] = useState<HandbookScene>('chain');
  useEffect(() => {
    if (shownScene === scene) return;
    const settle = window.setTimeout(() => setShownScene(scene), 130);
    return () => window.clearTimeout(settle);
  }, [scene, shownScene]);
  const [progress, setProgress] = useState(0);
  const chapterRefs = useRef(new Map<string, HTMLElement>());
  /** The reading column, so the anchor query never reaches outside it. */
  const articleRef = useRef<HTMLDivElement | null>(null);
  const railRefs = useRef(new Map<string, HTMLElement>());
  const [railPill, setRailPill] = useState<{ top: number; height: number } | null>(null);

  const chapters = useMemo(() => data?.chapters ?? [], [data]);
  const activeChapter = chapters.find((c) => c.id === activeId) ?? chapters[0];

  /* Which chapter owns the canvas, plus the reveal for each section as it
     arrives. One observer for "am I the chapter being read" (a band across
     the upper third), one for "have I been seen" (the entrance). */
  useEffect(() => {
    if (chapters.length === 0) return;
    const els = [...chapterRefs.current.values()];
    if (els.length === 0) return;

    const seen = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            (e.target as HTMLElement).dataset.in = 'true';
            seen.unobserve(e.target);
          }
        });
      },
      // Generous, and threshold 0: a chapter must be visible by the time it
      // reaches the fold, and a chapter taller than the viewport must never
      // need a percentage of itself on screen to stop being invisible.
      { rootMargin: '240px 0px 240px 0px', threshold: 0 },
    );
    // Chapter granularity only. DESIGN.md budgets one authored entrance per
    // surface; revealing every subsection as well turns the whole scroll into
    // the same animation played twenty times.
    //
    // The entrance is observed on an inner element, never on the section the
    // active-chapter observer below watches. An IntersectionObserver reports
    // *transformed* rects, so putting the reveal's translate on the observed
    // element makes the entrance move its own intersection, flip the active
    // chapter, and set the companion canvas flapping between scenes. That
    // actually happened and the screenshot caught three scenes drawn at once.
    els.forEach((el) => {
      const inner = el.firstElementChild;
      if (inner) seen.observe(inner);
    });

    return () => seen.disconnect();
  }, [chapters]);

  /* Reading progress, and which chapter owns the canvas.
   *
   * Both come off the one rAF-gated scroll pass. The active chapter was an
   * IntersectionObserver first, and it ran a full chapter behind: an observer
   * hands you the rect *as it was when the intersection changed*, so a set
   * built up across callbacks compares rects measured at different scroll
   * positions. Reading the live rects in one pass is the same amount of work
   * and is simply correct — the chapter that owns the page is the last one
   * whose heading has crossed the band line. */
  useEffect(() => {
    if (chapters.length === 0) return;
    let raf = 0;
    const measure = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0);

      const line = window.innerHeight * 0.3;
      let current: string | null = null;
      for (const ch of chapters) {
        const el = chapterRefs.current.get(ch.id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= line) current = ch.id;
      }
      // Above the first heading, the first chapter still owns the rail.
      setActiveId(current ?? chapters[0].id);

      // The panel anchors finer than the rail does. Every chapter and every
      // scene-declaring section carries `data-scene`; in document order the
      // last one past the line is what the reader is actually on.
      // Scoped to the reading column. A document-wide query also picks up
      // anything else that happens to carry the attribute — including the
      // panel's own canvas, which turns the anchor list into a feedback loop.
      const anchors = (articleRef.current ?? document).querySelectorAll<HTMLElement>('[data-scene]');
      let next: HandbookScene | null = null;
      // No early exit in either loop. Both lists are short, and bailing on the
      // first element past the line quietly assumes the rects stay monotonic
      // — which is the kind of assumption that turns one reflow into a rail
      // that sticks a chapter behind.
      let nextTop = -Infinity;
      for (const el of anchors) {
        const top = el.getBoundingClientRect().top;
        if (top <= line) {
          next = el.dataset.scene as HandbookScene;
          nextTop = top;
        }
      }
      // The rule is exactly "the last anchor above the line" — no distance
      // threshold. An earlier version demanded a new scene clear the line by
      // 24px before it could take over, which quietly held the panel on the
      // previous section whenever an anchor came to rest inside that band.
      // Jitter is absorbed by ChainCanvas's own debounce, which is the right
      // place for it: there it delays the animation without ever changing
      // which scene is correct.
      if (next) {
        sceneRef.current = next;
        setScene(next);
      }
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        measure();
      });
    };
    onScroll();
    // Scrolling re-measures continuously, so a reader is always current. The
    // gap is landing somewhere and stopping — a deep link, a rail click, a
    // restored scroll position — where the last measure can predate the final
    // reflow and leave the rail a chapter behind. Two cheap catch-up passes
    // close that window.
    // Early: a correction here starts a panel switch, and one arriving most
    // of a second after you land reads as the page changing its mind rather
    // than as it settling.
    const settle = [120, 450].map((ms) => window.setTimeout(onScroll, ms));
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    // Scroll and resize are not the only things that move a section: an
    // inline canvas taking its height, a line-reveal reserving its block, and
    // late webfonts all reflow the column underneath a stationary reader.
    // Without this the rail sticks on whichever chapter was correct before
    // the reflow — measured, at scroll 17000, one chapter behind.
    const ro = new ResizeObserver(onScroll);
    ro.observe(document.body);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      ro.disconnect();
      settle.forEach((t) => window.clearTimeout(t));
      if (raf) cancelAnimationFrame(raf);
    };
  }, [chapters]);

  /* The rail's sliding light. Measured from the real element with
     offsetTop/offsetHeight — the same lesson NavBar's pill records. */
  useEffect(() => {
    if (!activeChapter) return;
    const el = railRefs.current.get(activeChapter.id);
    if (!el) {
      setRailPill(null);
      return;
    }
    const next = { top: el.offsetTop, height: el.offsetHeight };
    setRailPill((prev) => (prev && prev.top === next.top && prev.height === next.height ? prev : next));
  }, [activeChapter]);

  const jumpTo = useCallback((id: string) => {
    const el = chapterRefs.current.get(id);
    if (!el) return;
    const offset = window.innerWidth >= 900 ? 100 : 150;
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - offset,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress size={26} sx={{ color: C.wormhole }} />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box sx={{ maxWidth: 620, mx: 'auto', mt: 6 }}>
        <ScanFrame color={C.red}>
          <PanelHeader label="Handbook unavailable" accent={C.red} dense />
          <Typography sx={{ fontFamily: FONT.body, fontSize: '0.92rem', color: C.text, lineHeight: 1.6 }}>
            The handbook could not be loaded. It is corp-only — if you are logged in on a character outside Evil Space
            Goblins or BorderZone Alliance, switch character and try again.
          </Typography>
        </ScanFrame>
      </Box>
    );
  }

  const langButton = (value: HandbookLang, label: string) => (
    <Box
      component="button"
      type="button"
      onClick={() => setLanguage(value)}
      aria-pressed={lang === value}
      sx={{
        appearance: 'none',
        border: 'none',
        cursor: 'pointer',
        px: 1.5,
        py: 0.4,
        borderRadius: 999,
        fontFamily: FONT.mono,
        fontSize: '0.72rem',
        letterSpacing: '0.1em',
        backgroundColor: lang === value ? hexToRgba(C.cyan, 0.16) : 'transparent',
        color: lang === value ? C.textBright : C.textDim,
        boxShadow: lang === value ? `0 0 10px ${hexToRgba(C.cyan, 0.28)} inset` : 'none',
        transition: 'background-color 0.22s ease, color 0.22s ease',
        '&:hover': { color: C.text },
        '&:focus-visible': { outline: `2px solid ${C.cyan}`, outlineOffset: '2px' },
      }}
    >
      {label}
    </Box>
  );

  return (
    // Grows with the display rather than sitting in a fixed 1180 gutter: on a
    // wide monitor the extra space goes to the reading column and the panel,
    // not to margin. The prose measure is held by the blocks themselves, so
    // widening the page widens the diagrams and the panel, never the lines.
    // MUI defaults: lg 1200, xl 1536 — nothing above, so xl carries every
    // display wider than that.
    <Box sx={{ maxWidth: { xs: '100%', lg: 1360, xl: 1860 }, mx: 'auto' }}>
      {/* ---------------------------------------------------------- masthead */}
      <ScanFrame color={C.wormhole} sx={{ px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: { xs: 3, md: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: FONT.display,
                fontWeight: 700,
                fontSize: { xs: '1.65rem', md: '2.5rem', xl: '3.1rem' },
                lineHeight: 1.08,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: C.textBright,
                textShadow: `0 0 26px ${hexToRgba(C.wormhole, 0.55)}`,
              }}
            >
              {say(data.title, lang)}
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT.sub,
                fontWeight: 600,
                fontSize: { xs: '0.95rem', md: '1.1rem' },
                color: C.cyan,
                mt: 0.75,
              }}
            >
              {say(data.subtitle, lang)}
            </Typography>
            <Typography sx={{ fontFamily: FONT.body, fontSize: '0.9rem', color: C.textDim, mt: 0.5 }}>
              {say(data.tagline, lang)}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              p: 0.35,
              borderRadius: 999,
              border: `1px solid ${C.hairline}`,
              backgroundColor: hexToRgba(C.surface, 0.7),
            }}
          >
            {langButton('en', 'EN')}
            {langButton('de', 'DE')}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            // minmax(0,…) on the single-column case too: a bare `1fr` track
            // takes its minimum from its content, so the masthead ASCII —
            // which is meant to scroll inside its own box — widened the whole
            // document by 40px at 390px instead. Caught the moment the K162
            // line got longer.
            gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1fr) minmax(0,1fr)' },
            gap: { xs: 2.5, md: 4 },
            mt: { xs: 3, md: 3.5 },
            alignItems: 'start',
          }}
        >
          <Box>
            <BlockView block={{ kind: 'diagram', caption: null, body: data.masthead.body }} lang={lang} />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: { xs: 2, md: 2.5 },
            }}
          >
            <MetaCell label={lang === 'de' ? 'Dokument' : 'Document'} value={data.document} />
            <MetaCell label={lang === 'de' ? 'Version' : 'Version'} value={data.version} />
            <MetaCell label={lang === 'de' ? 'Gültigkeit' : 'Valid from'} value={data.validFrom} />
            <MetaCell label={lang === 'de' ? 'Zielgruppe' : 'Audience'} value={say(data.audience, lang)} />
            <MetaCell label={lang === 'de' ? 'Einsatz' : 'Scope'} value={say(data.usage, lang)} />
            {/* The source document's masthead carries this value in a bare,
                unlabelled cell. Anoikis is the in-fiction name for W-space,
                so the honest label is what it describes, not "Region". */}
            <MetaCell label={lang === 'de' ? 'Raum' : 'Space'} value={data.region} />
          </Box>
        </Box>

        <Box sx={{ mt: { xs: 3, md: 3.5 } }}>
          <BlockView
            block={{ kind: 'callout', title: data.lead.title, body: data.lead.body }}
            lang={lang}
          />
        </Box>

        <Typography
          sx={{
            fontFamily: FONT.body,
            fontSize: '0.8rem',
            lineHeight: 1.6,
            color: C.textDim,
            mt: 2.5,
            maxWidth: '72ch',
          }}
        >
          {say(data.notice, lang)}
        </Typography>
      </ScanFrame>

      {/* ------------------------------------------------- mobile companion */}
      <Box
        sx={{
          // Up to `lg`, where the sticky companion column appears. Between md
          // and lg the rail exists but there is no room beside the text, so
          // the compact companion has to carry the canvas there too.
          display: { xs: 'block', lg: 'none' },
          position: 'sticky',
          top: { xs: STICKY_TOP.xs, md: STICKY_TOP.md },
          zIndex: 3,
          mb: 2,
          borderRadius: `${PANEL_RADIUS}px`,
          border: `1px solid ${C.hairline}`,
          // Opaque, not translucent: DESIGN.md's rule for anything floating
          // over the backdrop, and the first capture showed body text reading
          // straight through a 92%-alpha fill.
          backgroundColor: C.panel,
          ...depthSx(),
          overflow: 'hidden',
        }}
      >
        <Box sx={{ height: 2, backgroundColor: hexToRgba(C.text, 0.08) }}>
          <Box
            sx={{
              height: '100%',
              width: `${progress * 100}%`,
              backgroundColor: C.cyan,
              boxShadow: `0 0 8px ${hexToRgba(C.cyan, 0.7)}`,
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            px: 1,
            py: 0.75,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {chapters.map((ch) => {
            const on = ch.id === activeChapter?.id;
            return (
              <Box
                key={ch.id}
                component="button"
                type="button"
                onClick={() => jumpTo(ch.id)}
                aria-current={on ? 'true' : undefined}
                sx={{
                  appearance: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  flexShrink: 0,
                  px: 1.1,
                  py: 0.4,
                  borderRadius: 999,
                  fontFamily: FONT.mono,
                  fontSize: '0.72rem',
                  backgroundColor: on ? hexToRgba(C.cyan, 0.16) : 'transparent',
                  color: on ? C.textBright : C.textDim,
                  '&:focus-visible': { outline: `2px solid ${C.cyan}`, outlineOffset: '-2px' },
                }}
              >
                {ch.number === null ? '§' : String(ch.number).padStart(2, '0')}
              </Box>
            );
          })}
          <Box
            component="button"
            type="button"
            onClick={() => setCompanionOpen((v) => !v)}
            aria-expanded={companionOpen}
            aria-label={
              companionOpen
                ? lang === 'de'
                  ? 'Darstellung ausblenden'
                  : 'Hide visual representation'
                : lang === 'de'
                  ? 'Darstellung anzeigen'
                  : 'Show visual representation'
            }
            sx={{
              appearance: 'none',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              position: 'sticky',
              right: 0,
              ml: 'auto',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              px: 0.75,
              backgroundColor: C.panel,
              color: C.cyan,
              '&:focus-visible': { outline: `2px solid ${C.cyan}`, outlineOffset: '-2px' },
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 10 7"
              aria-hidden
              sx={{
                width: 10,
                height: 7,
                transform: companionOpen ? 'none' : 'rotate(180deg)',
                transition: prefersReducedMotion() ? 'none' : 'transform 0.25s cubic-bezier(0.16,1,0.3,1)',
              }}
            >
              <path d="M1 5.5L5 1.5L9 5.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </Box>
          </Box>
        </Box>
        {companionOpen && (
          <Box sx={{ px: 1.5, pb: 1 }}>
            <ChainCanvas scene={shownScene} height={150} />
            <Typography sx={{ ...lcarsLabelSx(hexToRgba(C.cyan, 0.85), '0.6rem'), mt: 0.5 }}>
              {say(SCENE_CAPTION[shownScene], lang)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ----------------------------------------------------- reading grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '176px minmax(0,1fr)',
            lg: '184px minmax(0,1fr) 400px',
            xl: '210px minmax(0,1fr) 520px',
          },
          gap: { xs: 0, md: 4 },
          alignItems: 'start',
        }}
      >
        {/* rail */}
        <Box
          component="nav"
          aria-label={lang === 'de' ? 'Kapitel' : 'Chapters'}
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'sticky',
            top: STICKY_TOP.md,
            maxHeight: `calc(100vh - ${STICKY_TOP.md + 24}px)`,
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: `${hexToRgba(C.wormhole, 0.4)} transparent`,
            pr: 1,
          }}
        >
          <Box sx={{ position: 'relative' }}>
            {railPill && (
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 4,
                  top: 0,
                  height: railPill.height,
                  transform: `translateY(${railPill.top}px)`,
                  borderRadius: '6px',
                  backgroundColor: hexToRgba(C.cyan, 0.12),
                  boxShadow: `0 0 12px ${hexToRgba(C.cyan, 0.22)} inset`,
                  transition: prefersReducedMotion()
                    ? 'none'
                    : 'transform 0.42s cubic-bezier(0.16,1,0.3,1), height 0.42s cubic-bezier(0.16,1,0.3,1)',
                  pointerEvents: 'none',
                }}
              />
            )}
            {chapters.map((ch) => {
              const on = ch.id === activeChapter?.id;
              return (
                <Box
                  key={ch.id}
                  component="button"
                  type="button"
                  ref={(el: HTMLElement | null) => {
                    if (el) railRefs.current.set(ch.id, el);
                    else railRefs.current.delete(ch.id);
                  }}
                  onClick={() => jumpTo(ch.id)}
                  aria-current={on ? 'true' : undefined}
                  sx={{
                    position: 'relative',
                    appearance: 'none',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    width: '100%',
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                    px: 1,
                    py: 0.7,
                    cursor: 'pointer',
                    borderRadius: '6px',
                    '&:focus-visible': { outline: `2px solid ${C.cyan}`, outlineOffset: '-2px' },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: FONT.mono,
                      fontSize: '0.68rem',
                      lineHeight: 1.5,
                      color: on ? C.cyan : hexToRgba(C.text, 0.45),
                      flexShrink: 0,
                      transition: 'color 0.22s ease',
                    }}
                  >
                    {ch.number === null ? '§' : String(ch.number).padStart(2, '0')}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: FONT.sub,
                      fontWeight: 600,
                      fontSize: '0.78rem',
                      lineHeight: 1.35,
                      color: on ? C.textBright : C.textDim,
                      transition: 'color 0.22s ease',
                    }}
                  >
                    {say(ch.title, lang)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* chapters */}
        {/* The tail lets the last chapter reach the reading line. Without it
            the document simply ends, the final section never crosses the
            line, and its panel state is unreachable — the closing scene could
            not be seen at all on a tall viewport. */}
        <Box ref={articleRef} sx={{ minWidth: 0, pb: { xs: '30vh', md: '55vh' } }}>
          {chapters.map((ch) => (
            <Box
              key={ch.id}
              id={ch.id}
              data-scene={ch.scene}
              component="section"
              ref={(el: HTMLElement | null) => {
                if (el) chapterRefs.current.set(ch.id, el);
                else chapterRefs.current.delete(ch.id);
              }}
              sx={{ scrollMarginTop: 110, mb: { xs: 5, md: 7 } }}
            >
              <Box sx={revealSx}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1 }}>
                {ch.number !== null && (
                  <Typography
                    sx={{
                      fontFamily: FONT.mono,
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      color: hexToRgba(C.cyan, 0.8),
                      flexShrink: 0,
                    }}
                  >
                    {String(ch.number).padStart(2, '0')}
                  </Typography>
                )}
                <Typography
                  component="h2"
                  sx={{
                    fontFamily: FONT.display,
                    fontWeight: 700,
                    fontSize: { xs: '1.2rem', md: '1.55rem', xl: '1.85rem' },
                    lineHeight: 1.2,
                    letterSpacing: '0.02em',
                    color: C.textBright,
                    textWrap: 'balance',
                  }}
                >
                  {say(ch.title, lang)}
                </Typography>
              </Box>
              <Box
                aria-hidden
                sx={{
                  height: '1px',
                  mb: { xs: 2.5, md: 3 },
                  background: `linear-gradient(90deg, ${hexToRgba(C.wormhole, 0.5)} 0%, ${hexToRgba(
                    C.wormhole,
                    0.08,
                  )} 55%, transparent 100%)`,
                }}
              />

              {/* Every section carries a scene, resolved here — never just the
                  ones that override. The panel takes the last anchor above the
                  reading line, so a section that emitted nothing silently
                  inherited the *previous section's* override instead of its
                  own chapter's: one tagged subsection made every later
                  subsection in that chapter keep showing the wrong diagram.
                  Falling back explicitly is what makes an override end where
                  its section does. */}
              {ch.sections.map((section, si) => (
                <Box
                  key={si}
                  data-scene={section.scene ?? ch.scene}
                  sx={{ mb: { xs: 3.5, md: 4.5 } }}
                >
                  {section.title && (
                    <Typography
                      component="h3"
                      sx={{
                        fontFamily: FONT.sub,
                        fontWeight: 700,
                        fontSize: { xs: '0.98rem', xl: '1.1rem' },
                        letterSpacing: '0.04em',
                        color: C.cyan,
                        mb: 1.75,
                      }}
                    >
                      {say(section.title, lang)}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 2.5 } }}>
                    {section.blocks.map((block, bi) => (
                      <BlockView key={bi} block={block} lang={lang} />
                    ))}
                  </Box>
                </Box>
              ))}
              </Box>
            </Box>
          ))}
        </Box>

        {/* companion canvas */}
        <Box
          sx={{
            display: { xs: 'none', lg: 'block' },
            position: 'sticky',
            top: STICKY_TOP.md,
          }}
        >
          <ScanFrame color={C.cyan} corners={false} sx={{ px: 1.5, py: 1.5 }}>
            {/* Not "chain model" any more: only three of the eight scenes are
                a chain. It is the visual representation of whatever the
                reader is currently on. */}
            <PanelHeader
              label={lang === 'de' ? 'Visuelle Darstellung' : 'Visual representation'}
              accent={C.cyan}
              dense
            />
            <ChainCanvas scene={shownScene} height={430} />
            <Typography
              sx={{
                fontFamily: FONT.body,
                fontSize: '0.78rem',
                lineHeight: 1.5,
                color: C.textDim,
                mt: 1.25,
                minHeight: '2.4em',
              }}
            >
              {say(SCENE_CAPTION[shownScene], lang)}
            </Typography>
            <Box sx={{ height: 2, borderRadius: 999, backgroundColor: hexToRgba(C.text, 0.08), mt: 1 }}>
              <Box
                sx={{
                  height: '100%',
                  borderRadius: 999,
                  width: `${progress * 100}%`,
                  backgroundColor: C.cyan,
                  boxShadow: `0 0 8px ${hexToRgba(C.cyan, 0.6)}`,
                }}
              />
            </Box>
          </ScanFrame>
        </Box>
      </Box>
    </Box>
  );
};

export default Handbook;
